import { generateToDocFn } from '@dadohr/shared/domainTypes/document';
import { IUserSavedView } from '@dadohr/shared/domainTypes/savedView';
import { FS } from '@dadohr/shared/versions';
import {
  CollectionListener,
  createCollectionListenerStore,
  useCollectionListener
} from '../firecache/collectionListener';
import {
  createDocumentListenerGetter,
  useDocumentListener
} from '../firecache/documentListener';
import { store, useCombineLoadingValues, useMappedLoadingValue } from '../firebase';
import { callFirebaseFunction } from '../firebaseFunctions';

const toSavedViewDoc = generateToDocFn<IUserSavedView>();

const STORE_KEY_SEP = '||';

const savedViewsByOrgAndUserStore = createCollectionListenerStore(
  (key: string) => {
    const [orgId, createdByUserId] = key.split(STORE_KEY_SEP);
    return new CollectionListener(
      store()
        .collection(FS.userSavedViews)
        .where('orgId', '==', orgId)
        .where('createdByUserId', '==', createdByUserId),
      toSavedViewDoc
    );
  }
);

export const useUserSavedViews = (orgId: string, createdByUserId: string) => {
  return useCollectionListener(
    savedViewsByOrgAndUserStore(`${orgId}${STORE_KEY_SEP}${createdByUserId}`)
  );
};

const sharedViewsByOrgAndUserStore = createCollectionListenerStore(
  (key: string) => {
    const [orgId, userId] = key.split(STORE_KEY_SEP);
    return new CollectionListener(
      store()
        .collection(FS.userSavedViews)
        .where('orgId', '==', orgId)
        .where('sharedWith', 'array-contains', userId),
      toSavedViewDoc
    );
  }
);

const useSharedUserSavedViews = (orgId: string, userId: string) => {
  return useCollectionListener(
    sharedViewsByOrgAndUserStore(`${orgId}${STORE_KEY_SEP}${userId}`)
  );
};

export const useAllUserSavedViews = (orgId: string, userId: string) => {
  return useMappedLoadingValue(
    useCombineLoadingValues(
      useUserSavedViews(orgId, userId),
      useSharedUserSavedViews(orgId, userId),
      { ignoreIntermediaryLoadingState: true }
    ),
    ([owned, shared]) => [...owned, ...shared]
  );
};

export type CreateSavedViewParams = {
  orgId: string;
  name: string;
  filters: IUserSavedView['filters'];
  columns: string[];
  sortKey: string;
  sortDirection: 'asc' | 'desc';
};

const savedViewByIdStore = createDocumentListenerGetter(
  (viewId) => store().collection(FS.userSavedViews).doc(viewId),
  toSavedViewDoc
);

export const useUserSavedViewById = (viewId: string) => {
  return useDocumentListener(savedViewByIdStore(viewId));
};

export const createUserSavedView = (params: CreateSavedViewParams) =>
  callFirebaseFunction<{ id: string }>('userSavedView_create', params);

export type UpdateSavedViewParams = {
  viewId: string;
  filters: IUserSavedView['filters'];
  columns: string[];
  sortKey: string;
  sortDirection: 'asc' | 'desc';
};

export const updateUserSavedView = (params: UpdateSavedViewParams) =>
  callFirebaseFunction<{ id: string }>('userSavedView_update', params);

export type ShareSavedViewParams = {
  viewId: string;
  userIds: string[];
};

export const shareSavedView = (params: ShareSavedViewParams) =>
  callFirebaseFunction<{ success: boolean }>('userSavedView_share', params);

export const renameUserSavedView = (params: {
  viewId: string;
  name: string;
}) => callFirebaseFunction<{ id: string }>('userSavedView_rename', params);

export type DeleteSavedViewResult = {
  id: string;
  action: 'deleted' | 'access-removed';
};

export const deleteUserSavedView = (viewId: string) =>
  callFirebaseFunction<DeleteSavedViewResult>('userSavedView_delete', {
    viewId
  });
